import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAdminAuth } from "@/lib/auth/admin-auth-context";

/**
 * Garde d'accès au back-office admin (`/admin/**`, sauf `/admin/connexion`) —
 * pendant client de apps/web/middleware.ts (`ADMIN_ACCESS_TOKEN_COOKIE`
 * vérifié côté edge Next.js). Ici, ce SPA n'a plus de runtime serveur pour
 * intercepter la navigation : la garde ne peut être qu'un composant React
 * monté au clic/à la navigation, qui redirige vers `/admin/connexion` si
 * `useAdminAuth().isLoggedIn` est faux.
 *
 * IMPORTANT — ceci est de l'UX, PAS de la sécurité : `isLoggedIn` ne reflète
 * qu'un indice local non fiable (cf. src/lib/auth/admin-auth-context.tsx,
 * localStorage, jamais le cookie httpOnly réel). N'importe qui peut
 * désactiver JS ou appeler l'API directement ; la seule vraie protection est,
 * comme aujourd'hui, côté serveur (les guards `apps/api`, inchangés par ce
 * lot). Ce composant évite seulement d'afficher un flash d'UI admin vide à un
 * visiteur non connecté.
 */
export function AdminRouteGuard({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAdminAuth();
  const location = useLocation();

  if (!isLoggedIn) {
    return <Navigate to="/admin/connexion" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
